import React, { useState, useEffect, useCallback } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { reportsAPI, clientAPI, userAPI, visitAPI, taskAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { FileText, Download, Filter, Calendar, User, Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, todayIST } from '../lib/utils';

/**
 * Consolidated Reports page — filter and download visit + task summaries.
 * Filters: type (all/visit/task), client, staff, date range.
 * Actions: per-row Download, plus "Download All (PDF)" for a single consolidated report.
 */
const ReportsPage = () => {
  usePageTitle('Reports');
  const [filters, setFilters] = useState({
    type: 'all', client_id: 'all', staff_id: 'all', from_date: '', to_date: '',
  });
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [data, setData] = useState({ visits: [], tasks: [], counts: { visits: 0, tasks: 0 } });
  const [loading, setLoading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);

  const buildParams = useCallback(() => {
    const p = {};
    if (filters.type !== 'all') p.type = filters.type;
    if (filters.client_id && filters.client_id !== 'all') p.client_id = filters.client_id;
    if (filters.staff_id && filters.staff_id !== 'all') p.staff_id = filters.staff_id;
    if (filters.from_date) p.from_date = filters.from_date;
    if (filters.to_date) p.to_date = filters.to_date;
    return p;
  }, [filters]);

  const runSearch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsAPI.list(buildParams());
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    clientAPI.getAll().then(r => setClients(r.data || [])).catch(() => {});
    userAPI.lookup({ role: 'staff' }).then(r => setStaff(r.data || [])).catch(() => {});
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadAll = async () => {
    setDownloadingAll(true);
    try {
      const res = await reportsAPI.downloadAll(buildParams());
      downloadBlob(new Blob([res.data], { type: 'application/pdf' }),
        `steward-consolidated-report-${todayIST()}.pdf`);
      toast.success('Consolidated report downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No summaries found or failed to download');
    } finally {
      setDownloadingAll(false);
    }
  };

  const handleDownloadSingle = async (kind, id) => {
    try {
      const res = kind === 'visit'
        ? await visitAPI.downloadSummary(id)
        : await taskAPI.downloadSummary(id);
      downloadBlob(new Blob([res.data], { type: 'application/pdf' }),
        `${kind}-${id.slice(0,8)}-summary.pdf`);
      toast.success('Downloaded');
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const totalItems = (data.counts?.visits || 0) + (data.counts?.tasks || 0);

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="w-6 h-6 text-indigo-600" /> Reports
          </h1>
          <p className="text-sm text-muted-foreground">Filter and download visit &amp; task summaries by client, staff, and date range.</p>
        </div>
        <Button
          onClick={handleDownloadAll}
          disabled={downloadingAll || totalItems === 0}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="download-consolidated-btn"
        >
          {downloadingAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Download All ({totalItems}) PDF
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Type</Label>
              <Select value={filters.type} onValueChange={v => setFilters(f => ({ ...f, type: v }))}>
                <SelectTrigger data-testid="report-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Visits + Tasks</SelectItem>
                  <SelectItem value="visit">Visits only</SelectItem>
                  <SelectItem value="task">Tasks only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1"><Building2 className="w-3 h-3" /> Client</Label>
              <Select value={filters.client_id} onValueChange={v => setFilters(f => ({ ...f, client_id: v }))}>
                <SelectTrigger data-testid="report-client-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1"><User className="w-3 h-3" /> Staff</Label>
              <Select value={filters.staff_id} onValueChange={v => setFilters(f => ({ ...f, staff_id: v }))}>
                <SelectTrigger data-testid="report-staff-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1"><Calendar className="w-3 h-3" /> From</Label>
              <Input type="date" value={filters.from_date}
                onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))}
                data-testid="report-from-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1"><Calendar className="w-3 h-3" /> To</Label>
              <Input type="date" value={filters.to_date}
                onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))}
                data-testid="report-to-date" />
            </div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => { setFilters({ type: 'all', client_id: 'all', staff_id: 'all', from_date: '', to_date: '' }); }}
              data-testid="report-reset-btn"
            >
              Reset
            </Button>
            <Button onClick={runSearch} disabled={loading} data-testid="report-apply-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Filter className="w-4 h-4 mr-2" />}
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results counts */}
      <div className="flex items-center gap-3 flex-wrap">
        <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-sm px-3 py-1">
          Visits: {data.counts?.visits || 0}
        </Badge>
        <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-sm px-3 py-1">
          Tasks: {data.counts?.tasks || 0}
        </Badge>
      </div>

      {/* Visits table */}
      {(filters.type === 'all' || filters.type === 'visit') && data.visits.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Visit Summaries ({data.visits.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Staff</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.visits.map(v => (
                    <TableRow key={v.id} data-testid={`report-visit-row-${v.id}`}>
                      <TableCell className="font-medium">{v.title}</TableCell>
                      <TableCell className="text-sm">{v.client_name || '-'}</TableCell>
                      <TableCell className="text-sm">{v.staff_names || '-'}</TableCell>
                      <TableCell className="text-sm">{formatDate(v.visit_date)}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{v.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => handleDownloadSingle('visit', v.id)} title="Download summary">
                          <Download className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tasks table */}
      {(filters.type === 'all' || filters.type === 'task') && data.tasks.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Task Summaries ({data.tasks.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.tasks.map(t => (
                    <TableRow key={t.id} data-testid={`report-task-row-${t.id}`}>
                      <TableCell className="font-medium">{t.title}</TableCell>
                      <TableCell className="text-sm">{t.client_name || '-'}</TableCell>
                      <TableCell className="text-sm">{t.staff_names || '-'}</TableCell>
                      <TableCell className="text-sm">{formatDate(t.completed_at || t.created_at)}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{t.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => handleDownloadSingle('task', t.id)} title="Download summary">
                          <Download className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {totalItems === 0 && !loading && (
        <Card>
          <CardContent className="text-center py-16 text-muted-foreground">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No summaries match your filters.</p>
            <p className="text-xs mt-1">Try widening the date range or removing filters.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportsPage;
